import { useEffect, useState } from 'react';
import type { Movie } from '@/mocks/movies';

interface PlayerAreaProps {
  movie: Movie;
}

type PlayerState = 'idle' | 'loading' | 'playing';

export default function PlayerArea({ movie }: PlayerAreaProps) {
  const [state, setState] = useState<PlayerState>('idle');
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (state !== 'playing') return undefined;
    const timer = window.setInterval(() => {
      setProgress((prev) => (prev >= 100 ? 0 : prev + 0.6));
    }, 120);
    return () => window.clearInterval(timer);
  }, [state]);

  const handlePlay = () => {
    if (state === 'playing') {
      setState('idle');
      return;
    }
    setState('loading');
    window.setTimeout(() => setState('playing'), 1200);
  };

  return (
    <div className="w-full">
      <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-background-950 border border-background-800">
        <img
          src={movie.backdrop}
          alt={`${movie.title} 播放画面`}
          title={`${movie.title} 在线观看`}
          className={`w-full h-full object-cover object-top transition-all duration-700 ${
            state === 'playing' ? 'opacity-40 blur-[1px]' : 'opacity-100'
          }`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/40 to-background-950/20"></div>

        {state === 'idle' && (
          <button
            type="button"
            onClick={handlePlay}
            className="absolute inset-0 flex flex-col items-center justify-center gap-4 group cursor-pointer"
            aria-label="播放演示"
          >
            <span className="relative w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary-500 text-background-50 flex items-center justify-center group-hover:scale-105 transition-transform">
              <span className="absolute inset-0 rounded-full bg-primary-500/60 animate-pulse-ring"></span>
              <i className="ri-play-fill text-3xl md:text-4xl relative z-10"></i>
            </span>
            <span className="text-sm md:text-base text-foreground-100 font-medium">点击开始播放（演示）</span>
          </button>
        )}

        {state === 'loading' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
            <span className="w-12 h-12 rounded-full border-2 border-foreground-700 border-t-primary-500 animate-spin"></span>
            <span className="text-sm text-foreground-200">正在加载演示画面…</span>
          </div>
        )}

        {state === 'playing' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-950/70 border border-primary-500/40 text-primary-300 text-xs md:text-sm">
              <i className="ri-movie-2-line"></i>
              前端演示播放中 · 无真实视频流
            </span>
          </div>
        )}

        <div className="absolute top-4 left-4 flex items-center gap-2">
          <span className="px-2.5 py-1 rounded-md bg-background-950/70 text-xs text-foreground-200">
            {movie.region}
          </span>
          <span className="px-2.5 py-1 rounded-md bg-background-950/70 text-xs text-foreground-200">
            {movie.duration}
          </span>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-3 md:gap-4">
        <button
          type="button"
          onClick={handlePlay}
          className="w-10 h-10 shrink-0 rounded-md bg-background-800 border border-background-700 flex items-center justify-center text-foreground-100 hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer"
          aria-label="播放或暂停"
        >
          <i className={state === 'playing' ? 'ri-pause-fill text-lg' : 'ri-play-fill text-lg'}></i>
        </button>
        <div className="flex-1 h-1.5 rounded-full bg-background-800 overflow-hidden">
          <div
            className="h-full bg-primary-500 transition-all duration-150"
            style={{ width: `${state === 'playing' ? progress : state === 'loading' ? 100 : 0}%` }}
          ></div>
        </div>
        <span className="text-xs text-foreground-400 whitespace-nowrap tabular-nums">
          {state === 'idle' ? '00:00' : '演示'} / {movie.duration.replace(' 分钟', ' min')}
        </span>
      </div>

      <p className="mt-3 text-xs text-foreground-500 flex items-start gap-2 leading-relaxed">
        <i className="ri-information-line mt-0.5 text-foreground-400"></i>
        播放区域为前端效果演示，仅模拟播放状态，不会加载或播放任何真实视频文件。
      </p>
    </div>
  );
}